import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { I } from '../icons.jsx';
import { TextField, Footer, AuthDecor, VividWordmark } from '../primitives.jsx';
import { api } from '../api.js';
import { storage } from '../storage.js';
import { handoffRedirect } from '../sso.js';
import { LangSwitch, useLang } from '../i18n.jsx';

// The page an invitation email links to (/invite/<token>[?lang=ar]).
//
// GET /auth/invitations/<token> says which company, who invited, the role
// and whether the invited address already has an account:
//   * new address  → first + last name and a password, which creates the
//                    account (email already verified by the link);
//   * existing one → that account's own password, which adds the company
//                    to it.
// Either way the answer is a normal sign-in (token for the invited
// company, refresh cookie), handed off exactly as the login page does.
// An invitation covering several companies (``companies`` in the preview)
// lists them all; accepting joins every one.
//
// Auth's own membership role ("member") is not shown: it says little
// about what the person will do in the ERP. The ERP's role names are,
// when the invitation carries them (``roleNames``).
//
// Language: ``?lang=`` (the ERP appends its current language), else the
// invitation's own ``lang``, else the one remembered here; Arabic is RTL.
const MIN_LENGTH = 8;
const MAX_NAME = 100;

const LINK_PROBLEMS = {
  INVITE_NOT_FOUND: 'invite.notFound',
  INVITE_EXPIRED: 'invite.expired',
  INVITE_REVOKED: 'invite.revoked',
  INVITE_ALREADY_ACCEPTED: 'invite.used',
};

export default function AcceptInvitePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const { lang, dir, t, tn, setLang } = useLang();
  const [invite, setInvite] = useState(null);
  // 'loading' | 'form' | 'problem'
  const [phase, setPhase] = useState('loading');
  // A LINK_PROBLEMS prefix, or { titleKey, bodyKey }.
  const [problem, setProblem] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getInvitation(token)
      .then((inv) => {
        if (cancelled) return;
        setInvite(inv);
        setFirstName(inv.firstName || '');
        setLastName(inv.lastName || '');
        // The language the invitation was sent in, unless the link says otherwise.
        if (inv.lang && !new URLSearchParams(window.location.search).get('lang')) setLang(inv.lang);
        setPhase('form');
      })
      .catch((err) => {
        if (cancelled) return;
        const prefix = LINK_PROBLEMS[err.code];
        setProblem(prefix
          ? { titleKey: `${prefix}.title`, bodyKey: `${prefix}.body` }
          : {
            titleKey: 'invite.openFailed.title',
            bodyKey: err.status === 429 ? 'invite.openFailed.busy' : 'invite.openFailed.body',
          });
        setPhase('problem');
      });
    return () => { cancelled = true; };
  }, [token, setLang]);

  const validate = () => {
    const e = {};
    if (invite?.userExists) {
      if (!password) e.password = t('invite.err.password');
      return e;
    }
    if (!firstName.trim()) e.firstName = t('invite.err.firstName');
    else if (firstName.trim().length > MAX_NAME) e.firstName = t('invite.err.tooLong');
    if (!lastName.trim()) e.lastName = t('invite.err.lastName');
    else if (lastName.trim().length > MAX_NAME) e.lastName = t('invite.err.tooLong');
    if (!password) e.password = t('invite.err.choosePassword');
    else if (password.length < MIN_LENGTH) e.password = t('common.minLength', { n: MIN_LENGTH });
    if (password && confirm !== password) e.confirm = t('common.mismatch');
    return e;
  };

  const tooMany = (err) => (err.retryAfter
    ? t('common.tryAgainIn', { mins: Math.max(1, Math.ceil(err.retryAfter / 60)) })
    : t('common.tooMany'));

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setServerErr('');
    setLoading(true);
    try {
      const body = invite.userExists
        ? { password }
        : { firstName: firstName.trim(), lastName: lastName.trim(), password };
      const res = await api.acceptInvitation(token, body);
      storage.saveAuth({ token: res.token, refreshToken: res.refreshToken, user: res.user });
      handoffRedirect({
        token: res.token,
        refreshToken: res.refreshToken,
        redirectTo: res.redirectTo,
        navigate,
      });
    } catch (err) {
      const fields = err.payload?.fields || {};
      if (LINK_PROBLEMS[err.code]) {
        setProblem({ titleKey: `${LINK_PROBLEMS[err.code]}.title`, bodyKey: `${LINK_PROBLEMS[err.code]}.body` });
        setPhase('problem');
      } else if (err.code === 'INVALID_CREDENTIALS') {
        setErrors({ password: t('invite.err.wrongPassword') });
      } else if (err.code === 'NAME_REQUIRED') {
        setErrors({ firstName: t('invite.err.firstName'), lastName: t('invite.err.lastName') });
      } else if (err.code === 'WEAK_PASSWORD') {
        setErrors({ password: t('common.minLength', { n: MIN_LENGTH }) });
      } else if (err.code === 'VALIDATION_ERROR' && Object.keys(fields).length) {
        // Server-side field problems (e.g. a name over 100 characters).
        const e2 = {};
        if (fields.firstName) e2.firstName = lang === 'en' ? fields.firstName : t('invite.err.tooLong');
        if (fields.lastName) e2.lastName = lang === 'en' ? fields.lastName : t('invite.err.tooLong');
        if (fields.password) e2.password = t('common.minLength', { n: MIN_LENGTH });
        if (Object.keys(e2).length) setErrors(e2);
        else setServerErr(t('invite.err.failed'));
      } else if (err.status === 429) {
        setServerErr(tooMany(err));
      } else {
        setServerErr(lang === 'en' && err.message ? err.message : t('invite.err.failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const shell = (children) => (
    <div dir={dir} lang={lang} className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="flex flex-col px-6 lg:px-12">
          <div className="h-20 flex items-center justify-between">
            <VividWordmark />
            <LangSwitch lang={lang} setLang={setLang} />
          </div>
          <div className="flex-1 flex items-start pt-[6vh] justify-center">
            <div className="w-full max-w-[420px]">{children}</div>
          </div>
          <Footer t={t} />
        </div>
        <div className="hidden lg:block"><AuthDecor t={t} /></div>
      </div>
    </div>
  );

  if (phase === 'loading') {
    return shell(
      <p role="status" aria-busy="true" className="text-[15px] text-stone-500">{t('invite.opening')}</p>,
    );
  }

  if (phase === 'problem') {
    return shell(
      <>
        <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
          <I.AlertCircle size={22} />
        </div>
        <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">{t(problem.titleKey)}</h1>
        <p role="alert" className="mt-2 text-[15px] text-stone-500">{t(problem.bodyKey)}</p>
        <Link to="/login" className="btn-primary w-full mt-8 inline-flex justify-center">{t('common.goToSignIn')}</Link>
      </>,
    );
  }

  const exists = invite.userExists;
  // One link may join several companies (a client with one company per
  // industry package): list them all.
  const companies = Array.isArray(invite.companies) && invite.companies.length > 0
    ? invite.companies
    : [{ orgId: invite.orgId, orgName: invite.orgName, role: invite.role }];
  const several = companies.length > 1;
  const joinLabel = several ? t('invite.nCompanies', { n: companies.length }) : invite.orgName;
  const roleNames = Array.isArray(invite.roleNames) ? invite.roleNames.filter(Boolean) : [];
  const strong = (text) => <span className="font-medium text-ink">{text}</span>;
  const inviter = invite.inviterName ? strong(invite.inviterName) : null;
  const pwdToggle = (
    <button type="button" className="p-1" aria-label={showPwd ? t('common.hidePassword') : t('common.showPassword')}
      onClick={() => setShowPwd((s) => !s)}>
      {showPwd ? <I.EyeOff size={16} /> : <I.Eye size={16} />}
    </button>
  );
  return shell(
    <form onSubmit={submit} noValidate>
      <div className="inline-flex w-12 h-12 rounded-full bg-stone-100 items-center justify-center text-ink">
        <I.MailCheck size={22} />
      </div>
      <h1 className="mt-5 text-[28px] font-bold tracking-tight text-ink">{t('invite.join', { org: joinLabel })}</h1>
      {several ? (
        <>
          <p className="mt-2 text-[15px] text-stone-500" data-testid="invite-intro">
            {inviter ? tn('invite.byInviterMany', { inviter }) : t('invite.neutralMany')}
          </p>
          <ul className="mt-3 space-y-1.5" data-testid="invite-companies">
            {companies.map((c) => (
              <li key={c.orgId} className="rounded-lg border border-stone-200 px-3 py-2 text-[14px]">
                <span className="font-medium text-ink">{c.orgName}</span>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="mt-2 text-[15px] text-stone-500" data-testid="invite-intro">
          {inviter
            ? tn('invite.byInviter', { inviter, org: strong(invite.orgName) })
            : tn('invite.neutral', { org: strong(invite.orgName) })}
        </p>
      )}
      {roleNames.length > 0 && (
        <p className="mt-2 text-[14px] text-stone-600" data-testid="invite-roles">
          {tn('invite.yourRole', { roles: strong(roleNames.join(lang === 'ar' ? '، ' : ', ')) })}
        </p>
      )}

      <div className="mt-7 space-y-4">
        <TextField label={t('common.email')} type="email" value={invite.email} readOnly autoComplete="username"
          helper={exists ? t('invite.helperExists') : t('invite.helperNew')} />
        {exists ? (
          <>
            <TextField label={t('invite.password')} type={showPwd ? 'text' : 'password'} value={password}
              onChange={setPassword} error={errors.password || serverErr || undefined}
              autoComplete="current-password" placeholder="••••••••"
              rightAdornment={pwdToggle} />
            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-[13px] font-medium text-ink hover:underline">
                {t('invite.forgot')}
              </Link>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <TextField label={t('invite.firstName')} value={firstName} onChange={setFirstName}
                error={errors.firstName || undefined} autoComplete="given-name" />
              <TextField label={t('invite.lastName')} value={lastName} onChange={setLastName}
                error={errors.lastName || undefined} autoComplete="family-name" />
            </div>
            <TextField label={t('invite.choosePassword')} type={showPwd ? 'text' : 'password'} value={password}
              onChange={setPassword} error={errors.password || undefined} autoComplete="new-password"
              helper={errors.password ? undefined : t('common.atLeast', { n: MIN_LENGTH })}
              rightAdornment={pwdToggle} />
            <TextField label={t('invite.confirmPassword')} type={showPwd ? 'text' : 'password'} value={confirm}
              onChange={setConfirm} error={errors.confirm || serverErr || undefined} autoComplete="new-password" />
          </>
        )}
      </div>

      <div className="pt-7">
        <button type="submit" disabled={loading} className="btn-primary w-full">
          {loading ? t('invite.joining') : t('invite.join', { org: joinLabel })}
        </button>
        <p className="mt-3 text-center text-[12px] text-stone-500">
          {t('invite.notYou')}
        </p>
      </div>
    </form>,
  );
}
